/**
 * Sidebar Footer Component
 * Bottom navigation items and logout button
 */

import { useLocation, useTranslation } from '@spcc/core'
import { useDispatch } from '@spcc/core'
import { isRtlLanguage } from '@spcc/portal-shared'
import {
  cn,
  LogOut,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@spcc/uikit'
import { authActions } from '@spcc/domains/auth'

// App version from build-time environment variable
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'dev'
import type { SidebarFooterProps } from '../types'
import { BOTTOM_MENU_ITEMS } from '../constants'
import { NavItem } from './nav-item'

// ============================================
// Helper Functions
// ============================================

function isPathActive(currentPath: string, itemPath: string): boolean {
  return currentPath === itemPath
}

function getLogoutButtonStyles(collapsed: boolean): string {
  const baseStyles = 'w-full flex items-center rounded-xl text-sm font-medium text-accent hover:bg-accent/10 dark:hover:bg-accent/20 transition-colors duration-200'
  const layoutStyles = collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3'

  return cn(baseStyles, layoutStyles)
}

// ============================================
// Sub-Components
// ============================================

interface LogoutButtonProps {
  collapsed: boolean
  isRTL: boolean
  onLogout: () => void
}

function LogoutButton({ collapsed, isRTL, onLogout }: LogoutButtonProps) {
  const { t } = useTranslation()
  const label = t('sidebar.logout')

  const button = (
    <button type="button" className={getLogoutButtonStyles(collapsed)} onClick={onLogout}>
      <LogOut className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  )

  if (!collapsed) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side={isRTL ? 'left' : 'right'} className="font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

// ============================================
// Component
// ============================================

export function SidebarFooter({ collapsed, onNavigate }: SidebarFooterProps) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const dispatch = useDispatch()
  const isRTL = isRtlLanguage(i18n.language)

  const handleLogout = () => {
    dispatch(authActions.logout())
    onNavigate?.()
  }

  return (
    <div className={cn('border-t border-border/50 transition-[padding,gap] duration-300', collapsed ? 'p-2' : 'p-4')}>
      <ul className="space-y-1.5">
        {BOTTOM_MENU_ITEMS.map((item) => (
          <li key={item.to}>
            <NavItem
              item={item}
              collapsed={collapsed}
              isActive={isPathActive(location.pathname, item.to)}
              isRTL={isRTL}
              onNavigate={onNavigate}
            />
          </li>
        ))}
        <li>
          <LogoutButton collapsed={collapsed} isRTL={isRTL} onLogout={handleLogout} />
        </li>
      </ul>
      {/* Version indicator */}
      <div className={cn(
        'mt-3 pt-3 border-t border-border/30 text-[10px] text-muted-foreground/60',
        collapsed ? 'text-center' : 'text-start'
      )}>
        {collapsed ? `v${APP_VERSION}` : `Version ${APP_VERSION}`}
      </div>
    </div>
  )
}
